import type { ReactNode } from 'react';
import {
  Flag, ListChecks,
  Link2, Database, List, Users, Cpu, BookOpen, ClipboardList, Activity,
  Sparkles, Boxes, FileText, SquarePen, MessageSquare, History, Gauge, Table2, Folder,
  Share2, Inbox, Bell, Globe,
} from 'lucide-react';
import { useAgentCard } from './AgentDock';
import { AREA_ICONS, isPendingSection, isVisibleSection } from '../lib/areas';
import type { WorkspaceSection } from '../lib/types';
import { useWorkspace } from '../contexts/WorkspaceContext';
import SourceLogo from './SourceLogo';
import { BUILTIN_SOURCES, isBuiltinSource, sourceLabel } from '../lib/sources';

// The top menus (Business, Knowledge, Reports, Sources, Agents, Admin),
// defined once and rendered twice: as dropdowns in the desktop top bar
// (Header.tsx) and as expandable groups in the phone menu drawer
// (MobileMenu.tsx). Add or change items here.

export type TopMenuEntry =
  | {
    kind: 'item'; label: ReactNode; icon?: ReactNode; depth?: 0 | 1;
    /** Needed where there is no `to` to key it by. */
    key?: string;
    to?: string;
    /** What choosing it does besides opening `to`, or on its own where there is no `to`. */
    onSelect?: () => void;
    hint?: ReactNode;
    /** Shown faded: a pending area, reachable so its questions can be answered. */
    muted?: boolean;
    title?: string;
  }
  | { kind: 'divider' };

export interface TopMenuGroup {
  label: string;
  entries: TopMenuEntry[];
}

const SOURCES_MENU_CAP = 8;
const icon = (Icon: typeof List) => <Icon className="w-3.5 h-3.5 shrink-0 text-coolslate" />;

function AreaProgress({ section }: { section: WorkspaceSection }) {
  const p = section.progress;
  if (!p || !p.total) return null;
  const tone =
    p.status === 'complete'
      ? 'bg-state-success text-navy'
      : p.status === 'in_progress'
        ? 'bg-signal text-navy'
        : 'bg-softgrey text-coolslate';
  return (
    <span className={`rounded-full px-1.5 py-px text-[10px] font-semibold leading-tight whitespace-nowrap ${tone}`}>
      {p.filled}/{p.total}
    </span>
  );
}

function areaEntry(section: WorkspaceSection): TopMenuEntry {
  const pending = isPendingSection(section);
  return {
    kind: 'item',
    key: `area-${section.id}`,
    to: `/workspace/${section.id}`,
    icon: icon(AREA_ICONS[section.id] ?? Folder),
    label: (
      <span className="inline-flex items-center gap-1.5">
        {section.name}
        {section.notification && <span className="w-1.5 h-1.5 rounded-full bg-signal" />}
      </span>
    ),
    hint: <AreaProgress section={section} />,
    muted: pending,
    title: pending ? `${section.name} - answer the relevance questions to enable this section` : section.name,
  };
}

function MenuCount({ n }: { n: number }) {
  return <span className="text-[11px] font-semibold px-1.5 rounded-full bg-brand/15 text-brand-dark">{n}</span>;
}

export function useTopMenus(): TopMenuGroup[] {
  const { sections, sources, chats, reviewItems } = useWorkspace();
  const { startChat, openChat, resetCard } = useAgentCard();

  const builtin = Object.keys(BUILTIN_SOURCES)
    .map(type => sources.find(s => s.source_type === type))
    .filter((s): s is NonNullable<typeof s> => !!s);
  const sortedSources = sources.filter(s => !isBuiltinSource(s.source_type)).sort((a, b) =>
    (b.updated_at || '').localeCompare(a.updated_at || ''),
  );
  const topSources = sortedSources.slice(0, SOURCES_MENU_CAP);
  const hasMoreSources = sortedSources.length > topSources.length;

  const menus: TopMenuGroup[] = [
    {
      label: 'Business',
      entries: sections.filter(isVisibleSection).map(areaEntry),
    },
    {
      label: 'Knowledge',
      entries: [
        { kind: 'item', to: '/knowledge', label: 'Records', icon: icon(Boxes) },
        { kind: 'item', to: '/review', label: 'Review', icon: icon(Inbox), hint: reviewItems ? <MenuCount n={reviewItems} /> : undefined },
        { kind: 'item', to: '/graph', label: 'Graph', icon: icon(Share2) },
        { kind: 'item', to: '/model', label: 'Model', icon: icon(Table2) },
        { kind: 'item', to: '/market', label: 'Market', icon: icon(Globe) },
        { kind: 'item', to: '/plans', label: 'Plans', icon: icon(Flag) },
        { kind: 'item', to: '/tasks', label: 'Tasks', icon: icon(ListChecks) },
        { kind: 'item', to: '/watches', label: 'Watches', icon: icon(Bell) },
        { kind: 'item', to: '/timeline', label: 'Timeline', icon: icon(History) },
      ],
    },
    {
      label: 'Reports',
      entries: [
        { kind: 'item', to: '/generated', label: 'Generated documents', icon: icon(FileText) },
        { kind: 'item', to: '/workspace', label: 'Document index', icon: icon(List) },
        { kind: 'item', to: '/library', label: 'Document Library', icon: icon(BookOpen) },
      ],
    },
    {
      label: 'Sources',
      entries: [
        ...builtin.map(s => ({
          kind: 'item' as const, key: `src-${s.id}`, to: `/sources/${s.id}`, label: sourceLabel(s.source_type),
          icon: <SourceLogo type={s.source_type} size="sm" alt="" />,
        })),
        ...(builtin.length > 0 ? [{ kind: 'divider' as const }] : []),
        ...topSources.map(s => ({
          kind: 'item' as const, key: `src-${s.id}`, to: `/sources/${s.id}`, label: s.source,
          icon: <SourceLogo type={s.source_type} size="sm" alt="" />,
        })),
        { kind: 'item', to: '/sources/add', label: 'Connect a new source', icon: icon(Link2) },
        { kind: 'divider' },
        { kind: 'item', to: '/sources', label: hasMoreSources ? 'See all sources' : 'All sources', icon: icon(Database) },
      ],
    },
    {
      label: 'Agents',
      entries: [
        { kind: 'item', key: 'new-chat', onSelect: () => startChat(), label: 'New agent', icon: icon(SquarePen) },
        ...(chats.length > 0 ? [{ kind: 'divider' as const }] : []),
        ...chats.map(c => ({
          kind: 'item' as const, key: `chat-${c.id}`, onSelect: () => openChat(c.id),
          label: c.origin ? `${c.title || 'Untitled chat'} (from ${c.origin})` : c.title || 'Untitled chat',
          icon: icon(MessageSquare),
        })),
        { kind: 'divider' },
        { kind: 'item', to: '/skills', label: 'Skills', icon: icon(Sparkles) },
      ],
    },
    {
      label: 'Admin',
      entries: [
        { kind: 'item', to: '/users', label: 'Users', icon: icon(Users) },
        { kind: 'item', to: '/usage', label: 'Usage', icon: icon(Gauge) },
        { kind: 'item', to: '/activity', label: 'Activity', icon: icon(ClipboardList) },
        { kind: 'item', to: '/jobs', label: 'Jobs', icon: icon(Cpu) },
        { kind: 'item', to: '/status', label: 'Status', icon: icon(Activity) },
      ],
    },
  ];

  // Only a chat opens the agent card maximised; every other entry resets the card so its page can be seen.
  return menus.map(group => ({
    ...group,
    entries: group.entries.map(e =>
      e.kind === 'item' && !e.onSelect ? { ...e, onSelect: resetCard } : e,
    ),
  }));
}
